import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Star, Quote } from 'lucide-react';
import { testimonials } from '../mock/mock';

const stories = [
  {
    couple: 'Aisha & Daniel',
    location: 'Austin, TX',
    headline: 'A 180-guest garden wedding, planned in 4 months.',
    guests: 180,
    budget: '$24k',
    body: 'After getting engaged with a tight timeline, Aisha and Daniel used Yourweddingfriends to keep their budget locked at $24k while still hosting a 180-guest outdoor reception. The vendor hub helped them save over $3,200 by comparing florists side by side.',
    image: 'https://images.unsplash.com/photo-1525441273400-056e9c7517b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHx3ZWRkaW5nJTIwZWxlZ2FudHxlbnwwfHx8fDE3Nzk5MTIwNjZ8MA&ixlib=rb-4.1.0&q=85',
  },
  {
    couple: 'Priya & Arjun',
    location: 'Seattle, WA',
    headline: 'A multi-day, two-country wedding with 480 guests.',
    guests: 480,
    budget: '$92k',
    body: 'Priya and Arjun ran a four-day multi-event wedding across Seattle and Mumbai. The dashboard kept both families on the same page — literally — across timezones, and the AI Wedding Friend helped translate vendor contracts in real time.',
    image: 'https://images.unsplash.com/photo-1502635385003-ee1e6a1a742d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHx3ZWRkaW5nJTIwZWxlZ2FudHxlbnwwfHx8fDE3Nzk5MTIwNjZ8MA&ixlib=rb-4.1.0&q=85',
  },
  {
    couple: 'Hannah & Theo',
    location: 'Denver, CO',
    headline: 'An intimate mountain elopement, zero stress.',
    guests: 22,
    budget: '$11k',
    body: 'A small ceremony at a lodge outside Denver. Hannah and Theo say the printable day-of timeline is what saved their reception when their coordinator got sick. Their replacement coordinator picked it up and “never missed a beat.”',
    image: 'https://images.unsplash.com/photo-1561848355-890d054dc55a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxicmlkYWwlMjBmbG93ZXJzfGVufDB8fHx8MTc3OTkxMjA2Nnww&ixlib=rb-4.1.0&q=85',
  },
];

export default function RealWeddings() {
  return (
    <div className="min-h-screen bg-[#FBF7F2]">
      <Navbar />
      <main className="pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <p className="section-eyebrow text-center">Real weddings</p>
          <h1 className="font-serif text-4xl lg:text-6xl text-center mt-3 text-[#2A2A2A] leading-tight">
            How real couples <span className="italic text-[#7B2C3B]">actually used it</span>.
          </h1>
          <p className="text-center mt-5 text-[#4A4A4A] max-w-2xl mx-auto">
            Stories from couples who planned their wedding with Yourweddingfriends — from intimate elopements to multi-day, multi-country celebrations.
          </p>

          <div className="mt-16 space-y-16">
            {stories.map((s, i) => (
              <div key={i} className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? 'lg:[&>div:first-child]:order-2' : ''}`}>
                <div className="aspect-[5/4] rounded-[2rem] overflow-hidden soft-card">
                  <img src={s.image} alt={s.couple} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="section-eyebrow">{s.couple} · {s.location}</p>
                  <h2 className="font-serif text-3xl lg:text-4xl mt-3 text-[#2A2A2A] leading-tight">{s.headline}</h2>
                  <div className="flex items-center gap-6 mt-5">
                    <div>
                      <p className="text-xs text-[#6A6A6A]">Guests</p>
                      <p className="font-serif text-2xl text-[#7B2C3B]">{s.guests}</p>
                    </div>
                    <div className="w-px h-10 bg-[#EFE3D8]" />
                    <div>
                      <p className="text-xs text-[#6A6A6A]">Total budget</p>
                      <p className="font-serif text-2xl text-[#7B2C3B]">{s.budget}</p>
                    </div>
                  </div>
                  <p className="mt-5 text-[#4A4A4A] leading-relaxed text-lg">{s.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-24">
            <p className="section-eyebrow text-center">Words from real couples</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-center mt-3 text-[#2A2A2A] mb-10">More happy tears — in their own words.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <div key={i} className="soft-card rounded-2xl p-7 relative">
                  <Quote className="w-7 h-7 text-[#F4D6CE] absolute top-5 right-5" />
                  <div className="flex items-center gap-1 text-[#B8896C] mb-4">
                    {[...Array(t.rating)].map((_, k) => <Star key={k} className="w-4 h-4 fill-[#B8896C]" />)}
                  </div>
                  <p className="text-[#2A2A2A] leading-relaxed text-[15px] mb-5 italic font-serif">“{t.text}”</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-[#EFE3D8]">
                    <div className="w-9 h-9 rounded-full bg-[#F4D6CE] flex items-center justify-center text-[#7B2C3B] font-serif text-sm font-semibold">{t.name.charAt(0)}</div>
                    <div>
                      <p className="text-sm font-medium text-[#2A2A2A]">{t.name}</p>
                      <p className="text-xs text-[#6A6A6A]">{t.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
